// English dictionary — base language. All keys must exist here.
export const en = {
    // ---- General ----
    "common.cancel": "Cancel",
    "common.save": "Save",
    "common.continue": "Continue",
    "common.delete": "Delete",
    "common.back": "Back",
    "common.close": "Close",
    "common.send": "Send",
    "common.loading": "Loading…",
    "common.send_link": "Email me a sign-in link",

    // ---- Auth ----
    "auth.welcome_back": "Welcome back",
    "auth.welcome_subtitle": "Sign in to {app}.",
    "auth.email": "Email",
    "auth.email_placeholder": "you@family.com",
    "auth.password": "Password",
    "auth.show": "Show",
    "auth.hide": "Hide",
    "auth.keep_signed": "Keep me signed in",
    "auth.forgot_password": "Forgot password?",
    "auth.no_account": "No account yet?",
    "auth.create_account": "Create account",
    "auth.have_account": "Already have an account?",
    "auth.sign_in": "Sign in",
    "auth.sign_up_title": "Create your account",
    "auth.sign_up_subtitle": "{app} keeps your household running quietly.",
    "auth.your_name": "Your name",
    "auth.first_name": "Your first name",
    "auth.household_name": "Your household's name",
    "auth.household_optional": "We'll auto-name it if you skip this.",
    "auth.continue_email": "Continue with email",
    "auth.invite_join": "Joining via invite",
    "auth.invite_code_label": "Invite code",
    "auth.use_password": "Use password instead",
    "auth.use_magic": "Use magic link instead",
    "auth.check_inbox": "Check your inbox",
    "auth.link_sent": "We sent a sign-in link to {email}. Tap it and you're in.",
    "auth.fresh_link_sent": "A fresh link is on its way to {email}. Tap it and you're in.",
    "auth.signing_in": "Signing you in…",
    "auth.almost_there": "Hold tight, almost there.",
    "auth.link_failed": "Link didn't work",
    "auth.link_invalid": "This link is invalid or expired.",
    "auth.send_fresh_to": "Send a fresh link to:",
    "auth.email_fresh_link": "Email me a fresh link",
    "auth.sending": "Sending…",
    "auth.back_to_signin": "Back to sign in",
    "auth.welcome_question": "Welcome — what should we call you?",
    "auth.first_time_caption": "First time signing in. We'll create your account.",
    "auth.forgot_title": "Reset your password",
    "auth.forgot_subtitle": "Enter your email — we'll send a reset link.",
    "auth.reset_email_sent": "Check your inbox — a reset link is on its way.",
    "auth.set_new_password": "Set a new password",
    "auth.new_password": "New password",

    // ---- App shell / nav ----
    "nav.today": "Today",
    "nav.week": "Menu",
    "nav.shopping": "Shopping",
    "nav.inventory": "Inventory",
    "nav.chores": "Chores",
    "nav.chat": "Chat",
    "header.your_thread": "Your thread with Milo",

    // ---- Overflow menu ----
    "menu.invite_member": "Invite a member",
    "menu.refer_friend": "Refer a friend",
    "menu.feedback": "Send feedback",
    "menu.telegram": "Reminders",
    "menu.pricing": "Plans & pricing",
    "menu.manage_subscription": "Manage subscription",
    "billing.toast.success": "Payment received — Milo Plus is now active. 🎉",
    "billing.toast.pending": "Checkout completed. We'll confirm shortly.",
    "billing.toast.confirm_failed": "Could not confirm Stripe session",
    "billing.toast.cancelled": "Checkout cancelled. You can try again anytime from Plans & pricing.",
    "billing.toast.no_customer": "No Stripe subscription on file yet. Visit Plans & pricing to start one.",
    "billing.toast.portal_failed": "Could not open billing portal",
    "pricing.current_plan": "Current plan",
    "pricing.cta_active": "You're on this plan",
    "pricing.checking_out": "Redirecting to Stripe…",
    "pricing.checkout_failed": "Could not start checkout. Please try again.",
    "plan.your_plan": "Your plan",
    "plan.free": "Free",
    "plan.plus": "Milo Plus",
    "plan.premium": "Milo Premium",
    "plan.trial": "Trial",
    "plan.active": "Active",
    "menu.set_password": "Set a password",
    "menu.admin": "Admin",
    "menu.language": "Language",
    "menu.sign_out": "Sign out",

    // ---- Welcome card (Today, empty state) ----
    "welcome.eyebrow": "Welcome",
    "welcome.headline": "Hi {name} — here's what I can do for {household}.",
    "welcome.subline": "Pick a starter below, or just talk to me. Most families get going in under 5 minutes.",
    "welcome.dismiss": "Dismiss",
    "welcome.starter.plan_dinners": "Plan dinners this week",
    "welcome.starter.plan_dinners_sub": "Tap a day, add a meal",
    "welcome.starter.add_shopping": "Add to shopping",
    "welcome.starter.add_shopping_sub": "One shared list, no duplicates",
    "welcome.starter.first_chore": "Set up a recurring chore",
    "welcome.starter.first_chore_sub": "Trash night, vacuum, bills…",
    "welcome.talk": "Or just talk to Milo",

    // ---- Today tab ----
    "today.eyebrow_prefix": "Today",
    "today.title": "Here's your day",
    "today.loading": "Loading today…",
    "today.chip.meal_assigned": "Dinner assigned",
    "today.chip.no_meal": "No meal yet",
    "today.chip.chores_due": "{n} chores due",
    "today.chip.chore_due": "{n} chore due",
    "today.chip.shopping_items": "{n} shopping items",
    "today.chip.shopping_item": "{n} shopping item",
    "today.todays_meal": "Today's meal",
    "today.nothing_planned": "Nothing planned yet",
    "today.tell_milo": "Tell Milo what's cooking",
    "today.cook_label": "Cook: {name}",
    "today.chores_due_today": "Chores due today",
    "today.see_all": "See all",
    "today.shopping_preview": "Shopping preview",
    "today.open_list": "Open list",
    "today.nothing_pressing": "Nothing pressing. Breathe.",
    "today.nothing_needed": "Nothing needed.",
    "today.more_count": "+{n} more",
    "today.urgent": "Urgent",
    "today.greeting_morning": "Good morning, {name}",
    "today.greeting_afternoon": "Good afternoon, {name}",
    "today.greeting_evening": "Good evening, {name}",

    // ---- Week tab ----
    "week.eyebrow": "Two-week menu",
    "week.title": "This week's menu",
    "week.loading": "Loading week…",
    "week.this_week": "This week",
    "week.next_week": "Next week",
    "week.today_marker": "today",
    "week.add_meal": "+ Add a meal",
    "week.role.cook": "Cook",
    "week.role.asst_cook": "Asst cook",
    "week.role.dishes": "Dishes",
    "week.role.asst_dishes": "Asst dishes",
    "week.placeholder_meal": "Pasta night",
    "week.select.cook": "Cook…",
    "week.select.dishes": "Dishes…",
    "week.select.asst_cook": "Asst cook…",
    "week.select.asst_dishes": "Asst dishes…",
    "week.add_assistants": "+ Add assistants",
    "week.recipe_placeholder": "Recipe / notes (optional)",
    "week.saved": "{day} saved",
    "week.assistant": "Assistant",

    // ---- Shopping ----
    "shop.eyebrow": "Shopping",
    "shop.title": "What do we need",
    "shop.add_placeholder": "Add milk, eggs, bread…",
    "shop.add_btn": "Add",
    "shop.any_store": "Any store",
    "shop.add_store_option": "+ Add store…",
    "shop.any_category": "Any category",
    "shop.personal_label": "Personal — only I can see this",
    "shop.personal_label_short": "Personal — only I see this",
    "shop.store_name_placeholder": "Store name (e.g. Whole Foods)",
    "shop.store_added": "Store added",
    "shop.couldnt_add_store": "Couldn't add store",
    "shop.sort_label": "Sort:",
    "shop.sort_label_short": "Sort",
    "shop.sort.newest": "Newest",
    "shop.sort.oldest": "Oldest",
    "shop.sort.store": "Store",
    "shop.sort.category": "Category",
    "shop.sort.alpha": "A–Z",
    "shop.sort.user": "User",
    "shop.bought": "Bought",
    "shop.bought_this_week": "Bought this week",
    "shop.bought_filter.this_week": "This week",
    "shop.bought_filter.last_week": "Last week",
    "shop.bought_filter.overall": "Overall",
    "shop.bought_filter.avg_per_week": "Avg / week",
    "shop.empty": "Your shopping list is empty.",
    "shop.empty_fridge": "Empty. Fridge must be thriving.",
    "shop.missing_count": "Missing items ({n})",
    "shop.no_period": "No items in this period.",
    "shop.avg_per_week_label": "items / week (avg)",
    "shop.tag_later": "Tag later",
    "shop.untagged": "Untagged",
    "shop.tagged_later": "Tagged for later",
    "shop.removed": "Removed",
    "shop.updated": "Updated",
    "shop.couldnt_update_item": "Couldn't update item",
    "shop.badge.personal": "personal",
    "shop.badge.later": "later",
    "shop.cat.Produce": "Produce",
    "shop.cat.Dairy": "Dairy",
    "shop.cat.Meat": "Meat",
    "shop.cat.Pantry": "Pantry",
    "shop.cat.Frozen": "Frozen",
    "shop.cat.Bakery": "Bakery",
    "shop.cat.Beverages": "Beverages",
    "shop.cat.Other": "Other",
    "shop.show_label": "Show",
    "shop.uncategorised": "Uncategorised",
    "shop.tag.groceries": "Groceries",
    "shop.tag.personal": "Personal",
    "shop.tag.later": "Later",
    "shop.tag.priority": "Priority",
    "shop.tag.home": "Home Improvement",
    "shop.fix_btn": "Fix my list",
    "shop.fix_btn_working": "Scanning…",
    "shop.fix_banner_one": "1 item is already in your pantry.",
    "shop.fix_banner_other": "{n} items are already in your pantry.",
    "shop.fix_check_all": "Check them all off",
    "shop.fix_dismiss": "Dismiss",
    "shop.fix_checked_toast_one": "1 item checked off",
    "shop.fix_checked_toast_other": "{n} items checked off",
    "shop.fix_no_matches": "Nothing already in stock — your list looks clean.",
    "shop.in_pantry": "In pantry",
    "shop.in_pantry_at": "In pantry · {location}",
    "shop.badge.priority": "Priority",
    "shop.badge.home": "Home",
    "recipes.title": "Family recipes",
    "recipes.eyebrow": "Recipe book",
    "recipes.add": "Add recipe",
    "recipes.empty": "No recipes yet. Add your family favourites — they'll appear in your meal planner with ratings and links.",
    "recipes.name": "Name",
    "recipes.name_placeholder": "e.g. Grandma's lasagna",
    "recipes.url": "Recipe URL",
    "recipes.url_placeholder": "https://…",
    "recipes.notes": "Notes",
    "recipes.notes_placeholder": "Ingredients, tips, family tweaks…",
    "recipes.rate": "Rate",
    "recipes.your_rating": "Your rating",
    "recipes.avg_rating": "Average",
    "recipes.ratings_count_one": "{n} rating",
    "recipes.ratings_count_other": "{n} ratings",
    "recipes.no_ratings": "No ratings yet",
    "recipes.delete_confirm": "Delete this recipe? Members who rated it will lose their votes.",
    "recipes.deleted": "Recipe deleted",
    "recipes.saved": "Recipe saved",
    "recipes.search_placeholder": "Search recipes…",
    "recipes.book_button": "Recipe book",
    "inventory.title": "Kitchen inventory",
    "inventory.eyebrow": "Pantry · fridge · freezer",
    "inventory.shop_button": "Kitchen inventory",
    "inventory.add_item": "Add item",
    "inventory.add_location": "Add storage",
    "inventory.empty_location": "Nothing in here yet. Tap Add item to log what's inside.",
    "inventory.empty_household": "Track what's in your fridge, pantry, and freezer so Milo can fix your shopping list and meal plan around it.",
    "inventory.all_tab": "All",
    "inventory.fields.name": "Item",
    "inventory.fields.name_placeholder": "e.g. Milk, Chicken breast, Rice",
    "inventory.fields.quantity": "Quantity",
    "inventory.fields.unit": "Unit",
    "inventory.fields.category": "Category",
    "inventory.fields.location": "Location",
    "inventory.fields.expiration": "Expires on",
    "inventory.fields.use_soon": "Use by",
    "inventory.fields.notes": "Notes",
    "inventory.fields.notes_placeholder": "Brand, store, freshness…",
    "inventory.location_name": "Storage name",
    "inventory.location_name_placeholder": "e.g. Fridge 2, Garage shelf, Basement",
    "inventory.location_icon": "Emoji (optional)",
    "inventory.delete_location_confirm": "Delete this storage location? Items inside will be deleted unless you move them first.",
    "inventory.delete_location_with_items": "This storage has {n} item(s). Pick another storage to move them to:",
    "inventory.delete_item_confirm": "Delete this item from inventory?",
    "inventory.sort.recent": "Recent",
    "inventory.sort.expiring": "Expiring soon",
    "inventory.sort.alpha": "Name",
    "inventory.sort.category": "Category",
    "inventory.expired": "Expired",
    "inventory.expires_today": "Expires today",
    "inventory.expires_in_days": "in {n}d",
    "inventory.use_soon_in_days": "use in {n}d",
    "inventory.search_placeholder": "Search inventory…",
    "inventory.quick_add_label": "Quick add",
    "inventory.quick_added": "added",
    "inventory.tab_eyebrow": "Inventory",
    "inventory.tab_title": "What do we have",
    "inventory.add_placeholder": "Add an item (e.g. Milk)",
    "inventory.add_btn": "Add",
    "inventory.open_shopping_btn": "Shopping list",
    "inventory.rename_hint": "Click to rename",
    "inventory.show_label": "SHOW",
    "inventory.sort_label": "SORT",
    "menu.inventory": "Kitchen inventory",
    "menu.recipes": "Family recipes",
    "week.save_as_recipe": "Save this meal as a family recipe",
    "week.recipe_url_label": "Recipe URL",
    "week.notes_label": "Notes",
    "week.open_recipe": "Open recipe ↗",
    "week.pick_recipe": "📖 Pick from recipes",
    "week.pick_for_me_btn": "🎲 Pick one for me",
    "week.pick_for_me_working": "Picking…",
    "week.pick_for_me_failed": "Couldn't find a recipe — try again",
    "week.pick_for_me_from_vault": "From your recipes: {name}",
    "week.pick_for_me_from_web": "Fresh pick: {name}",
    "week.servings_label": "Portions",
    "week.servings_default_hint": "default {n}",
    "week.add_to_shopping": "+ Add ingredients to shopping",
    "week.add_week_to_shopping": "+ Add all week to shopping",
    "week.ingredients_count_one": "{n} ingredient",
    "week.ingredients_count_other": "{n} ingredients",
    "week.steps_count_one": "{n} step",
    "week.steps_count_other": "{n} steps",
    "week.ai_generated_badge": "🤖 AI suggestion",
    "week.ai_generated_hint": "Generated by AI — no verified web source",
    "week.no_ingredients": "No ingredients on this recipe",
    "week.no_recipe_link": "Link a recipe first to add its ingredients",
    "week.added_to_shopping": "{added} added, {merged} merged",
    "week.week_added_to_shopping": "Week shopping ready: {added} added · {merged} merged",
    "week.picker_title": "Pick a recipe",
    "week.picker_empty": "No recipes yet. Add some in your Recipe book.",
    "week.picker_search": "Search recipes…",
    "week.hh_servings_label": "Household portions",
    "week.hh_servings_help": "How many people you usually cook for. Recipes scale to this.",
    "week.hh_servings_saved": "Default portions saved",
    "week.fix_btn": "Fix this week's menu",
    "week.fix_btn_working": "Planning your week…",
    "week.fix_settings_btn": "Menu settings",
    "week.fix_overwrite_label": "Replace meals already set this week",
    "week.fix_admin_only": "Only the family admin can run Fix my menu",
    "week.fix_done": "Week ready: {vault} from your recipes · {web} fresh picks",
    "week.fix_none": "All days are already set. Toggle 'Replace meals' to regenerate.",
    "week.fix_reshuffle_prompt": "All days are already planned. Reshuffle them?",
    "week.fix_reshuffle_now": "Reshuffle",
    "week.fix_settings_title": "Fix my menu — settings",
    "week.fix_vault_ratio_label": "Mix of your recipes vs. new ideas",
    "week.fix_vault_ratio_hint": "{vault}% from your recipe book · {web}% new picks from the web",
    "week.fix_priorities_label": "What matters most",
    "week.fix_priorities_hint": "Each priority is weighted from 0 (ignore) to 10 (top priority). Higher = more influence when Milo picks meals.",
    "week.fix_priority.uses_expiring": "Use items expiring soon",
    "week.fix_priority.high_rated": "Pick your top-rated recipes",
    "week.fix_priority.avoid_recent": "Avoid recently cooked meals",
    "week.fix_priority.uses_inventory": "Use what's already in stock",
    "week.fix_priority.balance_cooks": "Share the cooking among members",
    "week.fix_settings_saved": "Menu settings saved",
    "week.fix_auto_check_label": "Auto-check items already in stock",
    "week.fix_auto_check_hint": "When ON, 'Fix my list' silently checks matched items instead of showing a banner.",
    "week.hh_settings_title": "Household settings",
    "week.roles_section_title": "Auto-assign cooks & dishes",
    "week.roles_section_hint": "When you run Fix my menu, roles are rotated across eligible members.",
    "week.role_toggle.auto_assign_cook": "Auto-assign main cook",
    "week.role_toggle.auto_assign_asst_cook": "Auto-assign assistant cook",
    "week.role_toggle.auto_assign_dishwasher": "Auto-assign main dishwasher",
    "week.role_toggle.auto_assign_asst_dishwasher": "Auto-assign assistant dishwasher",
    "week.role_toggle.cook_dish_must_differ": "Cook and dishwasher must differ",
    "week.role_toggle.cook_dish_must_differ_hint": "Never the same person cooks AND does dishes the same night.",
    "week.family_section_title": "Family roles",
    "week.family_section_hint": "Toggle what each member can do. Kids who shouldn't cook stay out of cook rotation.",
    "week.member.can_cook": "Cook",
    "week.member.can_dish": "Dishes",
    "week.scrape_title": "Web recipes",
    "week.scrape_hint": "Fetch real ingredients from the source URL on Fix-my-menu picks (slower by ~5s, more accurate).",
    "week.scrape_toggle": "Extract ingredients from recipe URLs",
    "week.allow_no_url_toggle": "Allow AI recipes without a verified URL",
    "week.allow_no_url_hint": "When OFF (default), Milo only keeps web picks with a working URL. When ON, AI-generated recipes without a link are accepted.",
    "week.favorite_site_label": "Favorite cooking site (URL)",
    "week.favorite_site_placeholder": "https://www.ricardocuisine.com",
    "week.favorite_site_hint": "When set, Milo will lean toward picking recipes from this site when running Fix this week's menu. Leave empty to pick from any reputable cooking site.",
    "week.favorite_sites_label": "Favorite cooking sites (ranked)",
    "week.favorite_sites_hint": "Add up to 10 sites in priority order. #1 = strongest preference, then #2, etc. Use the arrows to reorder. Leave empty to pick from any reputable cooking site.",
    "week.favorite_site_invalid": "URL must start with http:// or https://",
    "week.erase_recipe_btn": "Erase recipe content",
    "week.erase_recipe_confirm": "Erase the meal name, URL, notes, ingredients and steps for this day?",
    "week.save_to_vault_btn": "⭐ Save to my recipes",
    "week.save_to_vault_saved": "Saved to your recipe book",
    "week.save_to_vault_already": "Already in your recipe book",
    "week.in_vault_badge": "📖 In the recipe vault",
    // ---- Ingredients editor (Recipe book) ----
    "recipes.servings_label": "Recipe yields",
    "recipes.servings_unit": "portions",
    "recipes.ingredients_title": "Ingredients",
    "recipes.ingredients_help": "Add what's needed. Quantities will auto-scale to your household portions when added to shopping.",
    "recipes.add_ingredient": "+ Add ingredient",
    "recipes.ingredient_name": "Ingredient",
    "recipes.ingredient_qty": "Qty",
    "recipes.ingredient_unit": "Unit",
    "recipes.ingredient_category": "Category",
    "recipes.ingredient_remove": "Remove",
    "recipes.unit.g": "g",
    "recipes.unit.kg": "kg",
    "recipes.unit.ml": "ml",
    "recipes.unit.l": "L",
    "recipes.unit.pcs": "pcs",
    "recipes.unit.tbsp": "tbsp",
    "recipes.unit.tsp": "tsp",
    "recipes.unit.cup": "cup",
    "recipes.unit.can": "can",
    "recipes.unit.bag": "bag",
    "recipes.unit.other": "other",
    "recipes.steps_title": "Steps",
    "recipes.steps_help": "Add the cooking instructions, one per line. Scraping a recipe URL fills these automatically.",
    "recipes.add_step": "+ Add step",
    "recipes.step_placeholder": "Describe this step…",
    "recipes.step_remove": "Remove step",

    // ---- Chores ----
    "chores.eyebrow": "Chores",
    "chores.title": "Who does what",
    "chores.add_placeholder": "Add a chore, e.g. take out trash…",
    "chores.add_btn": "Add",
    "chores.assign_to": "Assign to…",
    "chores.unassigned": "Unassigned",
    "chores.recurrence.none": "One-time chore",
    "chores.recurrence.daily": "Repeat daily",
    "chores.recurrence.weekly": "Repeat weekly",
    "chores.recurrence.biweekly": "Repeat every 2 weeks",
    "chores.recurrence.monthly": "Repeat monthly",
    "chores.filter.all": "All chores",
    "chores.filter.mine": "My chores",
    "chores.filter.done": "Completed",
    "chores.added": "Chore added",
    "chores.added_recurring": "Recurring chore added ({rec})",
    "chores.empty": "Nothing pending.",
    "chores.section.due_today": "Due today",
    "chores.section.tomorrow": "Tomorrow",
    "chores.section.upcoming": "Upcoming",
    "chores.save": "Save chore",
    "chores.needs_title": "Chore needs a title",
    "chores.couldnt_update": "Couldn't update chore",
    "chores.pushed_tomorrow": "Pushed to tomorrow",
    "chores.updated": "Chore updated",
    "chores.clean_slate": "Clean slate",
    "chores.empty_subtitle": "Nothing on the list. Enjoy the breather.",
    "chores.completed_count": "Completed ({n})",
    "chores.no_completed": "No completed chores yet.",
    "chores.urgent": "Urgent",
    "chores.edit_tooltip": "Edit",
    "chores.snooze_tooltip": "+1 day",
    "chores.repeats_tooltip": "Repeats {rec}",

    // ---- Chat ----
    "chat.eyebrow": "Your thread with Milo",
    "chat.title": "Just talk",
    "chat.placeholder": "Message the family…",
    "chat.empty": "Hi — try asking me to plan tonight's dinner, or 'add milk to the list'.",
    "chat.suggestions_intro": "Say things like:",
    "chat.suggestion.add_milk": "Add milk",
    "chat.suggestion.whats_dinner": "What's for dinner?",
    "chat.suggestion.show_shopping": "Show shopping list",
    "chat.suggestion.laundry_done": "Laundry done",
    "chat.suggestion.show_week": "Show week",
    "chat.error_milo_sleeping": "Milo is sleeping. Try again.",
    "chat.voice.unsupported": "Voice not supported in this browser. Try Chrome, Edge, or Safari.",
    "chat.voice.https": "Voice needs HTTPS. Open the deployed URL or preview in a new tab.",
    "chat.voice.not_allowed": "Mic blocked. If you're inside a preview panel, open the URL in a new tab and allow mic.",
    "chat.voice.service_not_allowed": "Mic blocked by browser/OS settings.",
    "chat.voice.no_speech": "Didn't catch that — try again.",
    "chat.voice.audio_capture": "No microphone found.",
    "chat.voice.network": "Network error reaching speech service.",
    "chat.voice.cant_start": "Couldn't start mic.",

    // ---- Feedback dialog ----
    "feedback.title": "Tell us what you think",
    "feedback.subtitle": "We read every reply.",
    "feedback.placeholder": "What's working? What's confusing or missing?",
    "feedback.bug": "Report a bug",
    "feedback.feature": "Feature request",
    "feedback.send": "Send feedback",
    "feedback.sent": "Thank you — got it.",

    // ---- Invite & refer ----
    "invite.title": "Invite a household member",
    "invite.subtitle": "Share this code or link.",
    "invite.copy": "Copy",
    "invite.copied": "Copied to clipboard",
    "refer.title": "Tell a friend about Milo",
    "refer.subtitle": "We'd love to meet more families.",

    // ---- Set password ----
    "setpw.title": "Set a password",
    "setpw.subtitle": "Optional — for faster sign-ins on this device.",
    "setpw.success": "Password set. You can now sign in with email + password.",

    // ---- Pricing page ----
    "pricing.eyebrow": "Plans",
    "pricing.headline": "Simple pricing for every household",
    "pricing.subline": "Start free. Upgrade when you're ready to let Milo do the thinking.",
    "pricing.most_popular": "Most popular",
    "pricing.per_month": "per month",
    "pricing.compare_title": "Compare features",
    "pricing.col_feature": "Feature",
    "pricing.footer": "Subscriptions launching soon. During the trial, everyone gets full access.",
    "pricing.cta_continue_free": "Continue free",
    "pricing.cta_try_plus": "Try Milo Plus",
    "pricing.cta_join_waitlist": "Join the waitlist",
    "pricing.tier.free.name": "Free",
    "pricing.tier.free.price": "$0",
    "pricing.tier.free.tag": "Daily essentials",
    "pricing.tier.plus.name": "Milo Plus",
    "pricing.tier.plus.price": "$15",
    "pricing.tier.plus.tag": "Save time + reduce mental load",
    "pricing.tier.premium.name": "Milo Premium",
    "pricing.tier.premium.price": "$25",
    "pricing.tier.premium.tag": "Full automation + savings",
    "pricing.group.core": "Daily basics",
    "pricing.group.smart": "Smart assistant",
    "pricing.group.premium_features": "Premium automation",
    "pricing.feat.chores": "Tasks & chores",
    "pricing.feat.grocery_manual": "Shared grocery list",
    "pricing.feat.meal_basic": "Basic meal planning",
    "pricing.feat.calendar_chat": "Calendar & family chat",
    "pricing.feat.households_1": "Multiple households",
    "pricing.feat.ai_meals": "AI meal planning",
    "pricing.feat.auto_grocery": "Auto-generated grocery list",
    "pricing.feat.voice_actions": "Voice → actions",
    "pricing.feat.smart_reminders": "Smart reminders & automation",
    "pricing.feat.ai_quota": "AI assistant",
    "pricing.feat.instacart": "Instacart integration (auto cart)",
    "pricing.feat.deals": "Deal & coupon optimization",
    "pricing.feat.budget": "Advanced AI & budgeting",
    "pricing.feat.insights": "Insights dashboard",
    "pricing.val.basic": "Basic",
    "pricing.val.high": "High",
    "pricing.val.unlimited": "Unlimited",

    // ---- Plan badges ----
    "plan.free": "Free",
    "plan.plus": "Plus",
    "plan.premium": "Premium",
    "plan.trial": "Trial",

    // ---- Admin billing ----
    "admin.billing.title": "Users & plans",
    "admin.billing.search": "Search by email or name…",
    "admin.billing.col.user": "User",
    "admin.billing.col.plan": "Plan",
    "admin.billing.col.status": "Status",
    "admin.billing.col.expires": "Manual expiry",
    "admin.billing.col.households": "Households",
    "admin.billing.col.last_active": "Last active",
    "admin.billing.col.actions": "Actions",
    "admin.billing.never": "Never",
    "admin.billing.set_plan": "Set plan",
    "admin.billing.clear_expiry": "Clear expiry",
    "admin.billing.updated": "Updated",

    // ---- Errors ----
    "err.generic": "Something went wrong. Try again.",
    "err.invalid_email": "Enter a valid email",
    "err.couldnt_send": "Couldn't send link",

    // ---- Household picker ----
    "picker.creator_badge": "Owner",
    "picker.delete_household": "Delete household",
    "picker.leave_household": "Leave household",
    "picker.delete_confirm": "This will permanently delete \"{name}\" and ALL its data (meals, chores, shopping, chat). Type the household name to confirm:",
    "picker.delete_name_mismatch": "Name didn't match — deletion cancelled.",
    "picker.deleted": "Deleted \"{name}\".",
    "picker.leave_confirm": "Leave \"{name}\"? Other members will still see it.",
    "picker.left": "You left \"{name}\".",

    // ---- Invite & refer ----
    "invite.eyebrow": "Invite family",
    "invite.join_headline": "Join {name}",
    "invite.or_share_code": "Or share the code",
    "invite.share": "Share",
    "invite.footer": "They scan → pick a name → done. No password.",
    "invite.link_copied": "Invite link copied",
    "invite.share_title": "Join {name} on Milo",
    "invite.share_text": "Join our Milo household \"{name}\". Code: {code}",
    "invite.share_cancelled": "Share cancelled",
    "invite.copy": "Copy",
    "invite.copied": "Copied to clipboard",
    "refer.headline": "Love Milo?",
    "refer.subline": "Share it with another family",
    "refer.message": "Hey! I've been using Milo to keep our household in sync — meals, chores, shopping, and family chat all in one warm little app. Thought you'd love it too. Give it a try: {url}",
    "refer.copy_btn": "Copy message",
    "refer.copied_btn": "Copied!",
    "refer.share_btn": "Share",
    "refer.share_title": "Milo — your household's quiet operator",
    "refer.copied": "Copied — paste it anywhere",
    "refer.copy_failed": "Couldn't copy — try selecting manually",
    "refer.footer": "Thank you for helping Milo grow, one family at a time.",

    // ---- Set password dialog ----
    "setpw.set_headline": "Set a password",
    "setpw.change_headline": "Change password",
    "setpw.set_sub": "Optional — faster sign-in next time",
    "setpw.change_sub": "Pick a new one — takes effect immediately",
    "setpw.new_password": "New password",
    "setpw.placeholder": "At least 6 characters",
    "setpw.too_short": "Password must be at least 6 characters",
    "setpw.saved": "Password set — use it next time",
    "setpw.updated": "Password updated",
    "setpw.save_failed": "Couldn't save password",
    "setpw.saving": "Saving…",
    "setpw.save_btn": "Save password",
    "setpw.update_btn": "Update password",
    "setpw.footer": "Magic link sign-in still works — this is just an optional shortcut.",

    // ---- Feedback dialog ----
    "feedback.headline": "Send feedback",
    "feedback.intro": "Milo's in beta — your input shapes it. Tell us what's working, what's broken, what you'd love.",
    "feedback.rating_q": "How's your experience so far?",
    "feedback.tap_star": "Tap a star",
    "feedback.rating1": "Ouch",
    "feedback.rating2": "Meh",
    "feedback.rating3": "Okay",
    "feedback.rating4": "Great",
    "feedback.rating5": "Love it",
    "feedback.bug_q": "Found a bug?",
    "feedback.bug_placeholder": "Describe what went wrong…",
    "feedback.feature_q": "Feature request?",
    "feedback.feature_placeholder": "What would make Milo better for you?",
    "feedback.notes_q": "Anything else?",
    "feedback.notes_placeholder": "Share your thoughts, encouragement, or anything…",
    "feedback.send_btn": "Send feedback",
    "feedback.sending": "Sending…",
    "feedback.empty": "Add a rating or a note first",
    "feedback.sent": "Thanks — we'll read every word.",
    "feedback.send_failed": "Couldn't send feedback. Try again.",
    "feedback.footer": "Linked to your account so we can follow up if needed.",

    // ---- Telegram ----
    "telegram.eyebrow": "Reminders",
    "telegram.title": "Get reminders on your phone",
    "telegram.intro": "Milo will send you a daily rundown and chore reminders through Telegram. No extra app needed.",
    "telegram.no_telegram": "Don't have Telegram?",
    "telegram.download": "Get it here",
    "telegram.get_link": "Turn on reminders",
    "telegram.open_telegram": "Open Telegram",
    "telegram.expires": "Link expires in {min} min.",
    "telegram.connected": "Reminders on",
    "telegram.connected_body": "You'll get a daily rundown at 4pm ET and reminders for today's chores.",
    "telegram.disconnect": "Turn off reminders",
    "telegram.disconnected": "Reminders turned off",
    "telegram.confirm_disconnect": "Turn off reminders?",
    "telegram.not_configured_title": "Not configured",
    "telegram.not_configured_body": "Your admin hasn't set up reminders yet.",
};
